import { z } from "zod";

/** Every error the API returns has this shape. */
export const ApiError = z.object({
  code: z.string(),
  message: z.string(),
  details: z.unknown().optional(),
});
export type ApiError = z.infer<typeof ApiError>;

export const HealthResponse = z.object({
  ok: z.literal(true),
  simVersion: z.string(),
});
export type HealthResponse = z.infer<typeof HealthResponse>;

/**
 * One schema per endpoint goes here, and the server route and the client fetch
 * wrapper both import it — that is what keeps 05_Data_Model_and_API.xlsx, the
 * server and the client from drifting apart (05 §14, change control).
 */

// ---------------------------------------------------------------------------
// Auth (S5-1). Contract from 05_Data_Model_and_API.xlsx, API sheet.
// ---------------------------------------------------------------------------

export const PLATFORMS = ["web", "android", "ios"] as const;

/**
 * What the client tells us about itself when it asks for a guest account.
 *
 * The fingerprint is NOT an identity and is never trusted for auth — it is
 * stored hashed, purely so that "one person, forty accounts" is visible later
 * (05 Entities: device.fingerprint_hash, "multi-account heuristics").
 */
export const GuestAuthRequest = z.object({
  device: z.object({
    platform: z.enum(PLATFORMS),
    fingerprint: z.string().min(1).max(200),
  }),
});
export type GuestAuthRequest = z.infer<typeof GuestAuthRequest>;

export const SessionTokens = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  /** Seconds until the access token expires, so the client can refresh early. */
  expiresIn: z.number().int().positive(),
});
export type SessionTokens = z.infer<typeof SessionTokens>;

export const GuestAuthResponse = SessionTokens.extend({
  accountId: z.string().uuid(),
  playerId: z.string().uuid(),
  villageId: z.string().uuid(),
});
export type GuestAuthResponse = z.infer<typeof GuestAuthResponse>;

export const RefreshRequest = z.object({ refreshToken: z.string().min(1) });
export type RefreshRequest = z.infer<typeof RefreshRequest>;

export const RefreshResponse = SessionTokens;
export type RefreshResponse = z.infer<typeof RefreshResponse>;

// ---------------------------------------------------------------------------
// Username + password (D-116). Brought forward from 05's Phase 2 /auth/link.
// ---------------------------------------------------------------------------

/** Letters, digits and underscores; 3–20 long. Shown on the map as the player's name. */
export const USERNAME_PATTERN = /^[A-Za-z0-9_]{3,20}$/u;
export const PASSWORD_MIN = 8;

export const Username = z
  .string()
  .trim()
  .regex(USERNAME_PATTERN, "3–20 letters, digits or _")
  .refine((name) => !/^guest/iu.test(name), "Names starting with “guest” are reserved");
export const Password = z.string().min(PASSWORD_MIN, `At least ${PASSWORD_MIN} characters`).max(200);

/** Put a username and password on the account this session belongs to. */
export const RegisterRequest = z.object({ username: Username, password: Password });
export type RegisterRequest = z.infer<typeof RegisterRequest>;

/** A village the account owns, for the choose-your-village screen. */
export const OwnedVillage = z.object({
  villageId: z.string().uuid(),
  name: z.string(),
  x: z.number().int(),
  y: z.number().int(),
});
export type OwnedVillage = z.infer<typeof OwnedVillage>;

export const AccountResponse = z.object({
  accountId: z.string().uuid(),
  /** Null while the account is still a guest. */
  username: z.string().nullable(),
  /** One per account in Phase 0 (D-117); empty until a village is founded. */
  villages: z.array(OwnedVillage).default([]),
});
export type AccountResponse = z.infer<typeof AccountResponse>;

/** Open a saved village on this device. */
export const LoginRequest = z.object({
  username: z.string().trim().min(1).max(40),
  password: z.string().min(1).max(200),
  device: GuestAuthRequest.shape.device,
});
export type LoginRequest = z.infer<typeof LoginRequest>;

/** A new account is not a village yet: both ids stay null until one is founded (D-117). */
export const LoginResponse = SessionTokens.extend({
  accountId: z.string().uuid(),
  playerId: z.string().uuid().nullable(),
  villageId: z.string().uuid().nullable(),
  username: z.string(),
});
export type LoginResponse = z.infer<typeof LoginResponse>;

/** Create an account with a username and password; no village yet (D-117). */
export const SignupRequest = z.object({
  username: Username,
  password: Password,
  device: GuestAuthRequest.shape.device,
});
export type SignupRequest = z.infer<typeof SignupRequest>;

/**
 * Letters in any script, digits, spaces, apostrophes, hyphens, underscores and
 * dots; 3–24 long. Underscores because the suggested name is "<username>'s
 * village" and usernames may contain them.
 */
export const VillageName = z
  .string()
  .trim()
  .min(3, "At least 3 characters")
  .max(24, "At most 24 characters")
  .regex(/^[\p{L}\p{N}][\p{L}\p{N} '’\-_.]*$/u, "Letters, digits, spaces and ' - _ . only");

export const FoundVillageRequest = z.object({ name: VillageName });
export type FoundVillageRequest = z.infer<typeof FoundVillageRequest>;

export const FoundVillageResponse = OwnedVillage.extend({ playerId: z.string().uuid() });
export type FoundVillageResponse = z.infer<typeof FoundVillageResponse>;
